// Platform promo codes & gift cards — the words every plane shares
// (docs/payment-module/platform-offers-api-contract.md): the signup page, the Billing page
// and the super-admin Gift & Promo screen all describe a promo's terms, a redemption's
// state and a refusal the same way, so the sentences live once, here.
//
// `platformOffers.error.<CODE>` is the one translation of each §2 code
// (platformOffers/errors.js). PLATFORM_CODE_INVALID deliberately does not say WHICH
// refusal it is: the server answers one opaque code so nobody can probe for valid codes.
//
// A promo is counted in PAID invoices (§5.0): during the trial it is scheduled, not
// attached, so every duration sentence says "paid".
export default {
  fr: {
    "platformOffers.error.PLATFORM_CODE_INVALID": "Ce code n'est pas valable ici.",
    "platformOffers.error.PLATFORM_CODE_RACE": "Ce code vient d'être utilisé en même temps ailleurs. Réessayez.",
    "platformOffers.error.NO_ACTIVE_SUBSCRIPTION": "Un code promo s'applique à un abonnement actif, et ce cabinet n'en a pas.",
    "platformOffers.error.NO_STRIPE_CUSTOMER": "Ajoutez d'abord un moyen de paiement : le crédit d'une carte cadeau s'inscrit sur votre compte de facturation.",
    "platformOffers.error.PROMO_CODE_EXISTS": "Ce code existe déjà.",
    "platformOffers.error.PROMO_TERMS_INVALID": "Les conditions de cette promo sont incohérentes : vérifiez le type, le montant, la durée, les forfaits et les dates.",
    "platformOffers.error.GIFT_CARD_NOT_VOIDABLE": "Cette carte ne peut plus être annulée : elle est réservée, déjà utilisée ou déjà annulée.",
    "platformOffers.error.GIFT_CARD_VOIDED": "Cette carte est annulée : il n'y a rien à renvoyer.",
    "platformOffers.error.PAYMENT_PROVIDER_UNAVAILABLE": "Le service de paiement a refusé l'opération. Rien n'a été modifié : réessayez dans quelques instants.",
    "platformOffers.error.REDEMPTION_OUTCOME_UNKNOWN": "Le service de paiement n'a pas pu confirmer l'opération. Votre code reste réservé et sera appliqué ou libéré automatiquement dans l'heure : inutile de réessayer.",
    "platformOffers.error.NOT_FOUND": "Ce code n'existe plus.",
    "platformOffers.error.RATE_LIMITED": "Trop d'essais : réessayez dans une minute.",
    "platformOffers.error.generic": "Ce code n'a pas pu être vérifié. Réessayez.",
    "platformOffers.code.labelAny": "Code promo ou carte cadeau",
    "platformOffers.code.labelPromo": "Code promo",
    "platformOffers.code.labelGiftcard": "Code de carte cadeau",
    "platformOffers.code.placeholder": "Saisissez votre code",
    "platformOffers.code.apply": "Appliquer",
    "platformOffers.code.remove": "Retirer",
    "platformOffers.code.kindRefused": "Ce code ne peut pas être utilisé ici.",
    "platformOffers.offer.percentOff": "{percent} de remise",
    "platformOffers.offer.amountOff": "{amount} de remise",
    "platformOffers.offer.credit": "{amount} de crédit",
    "platformOffers.offer.perInvoice": "{from} → {to} par facture",
    "platformOffers.duration.once": "sur la première facture payée",
    "platformOffers.duration.repeating": "pendant {months} mois payés",
    "platformOffers.duration.repeatingOne": "pendant 1 mois payé",
    "platformOffers.duration.forever": "sur chaque facture payée",
    "platformOffers.redemption.status.held": "Réservé",
    "platformOffers.redemption.status.scheduled": "Commence à la fin de l'essai",
    "platformOffers.redemption.status.applied": "Appliqué",
    "platformOffers.redemption.status.released": "Libéré",
    "platformOffers.redemption.reason.checkout_expired": "paiement non finalisé",
    "platformOffers.redemption.reason.stripe_failed": "refusé par le service de paiement",
    "platformOffers.redemption.reason.sweep": "réservation expirée",
    "platformOffers.redemption.reason.superseded": "remplacé par un code plus récent",
    "platformOffers.redemption.reason.subscription_ended": "abonnement terminé",
    "platformOffers.redemption.reason.request_failed": "demande interrompue",
    "platformOffers.redemption.reason.practice_deleted": "cabinet supprimé",
    "platformOffers.redemption.surface.signup": "Inscription",
    "platformOffers.redemption.surface.plan_change": "Changement de forfait",
    "platformOffers.redemption.surface.billing_redeem": "Page Facturation",
    "platformOffers.redemption.kind.promo": "code promo",
  },
  en: {
    "platformOffers.error.PLATFORM_CODE_INVALID": "This code is not valid here.",
    "platformOffers.error.PLATFORM_CODE_RACE": "This code was just used at the same moment elsewhere. Please try again.",
    "platformOffers.error.NO_ACTIVE_SUBSCRIPTION": "A promo code applies to an active subscription, and this practice has none.",
    "platformOffers.error.NO_STRIPE_CUSTOMER": "Add a payment method first: a gift card's credit is recorded on your billing account.",
    "platformOffers.error.PROMO_CODE_EXISTS": "This code already exists.",
    "platformOffers.error.PROMO_TERMS_INVALID": "These promo terms are inconsistent: check the type, amount, duration, plans and dates.",
    "platformOffers.error.GIFT_CARD_NOT_VOIDABLE": "This card can no longer be voided: it is reserved, already redeemed or already voided.",
    "platformOffers.error.GIFT_CARD_VOIDED": "This card is voided: there is nothing to resend.",
    "platformOffers.error.PAYMENT_PROVIDER_UNAVAILABLE": "The payment service declined the request. Nothing was changed: try again in a moment.",
    "platformOffers.error.REDEMPTION_OUTCOME_UNKNOWN": "The payment service could not confirm this. Your code stays reserved and will be applied or released automatically within the hour, so there is no need to try again.",
    "platformOffers.error.NOT_FOUND": "This code no longer exists.",
    "platformOffers.error.RATE_LIMITED": "Too many attempts: try again in a minute.",
    "platformOffers.error.generic": "This code could not be checked. Please try again.",
    "platformOffers.code.labelAny": "Promo code or gift card",
    "platformOffers.code.labelPromo": "Promo code",
    "platformOffers.code.labelGiftcard": "Gift card code",
    "platformOffers.code.placeholder": "Enter your code",
    "platformOffers.code.apply": "Apply",
    "platformOffers.code.remove": "Remove",
    "platformOffers.code.kindRefused": "This code cannot be used here.",
    "platformOffers.offer.percentOff": "{percent} off",
    "platformOffers.offer.amountOff": "{amount} off",
    "platformOffers.offer.credit": "{amount} credit",
    "platformOffers.offer.perInvoice": "{from} → {to} per invoice",
    "platformOffers.duration.once": "on the first paid invoice",
    "platformOffers.duration.repeating": "for {months} paid months",
    "platformOffers.duration.repeatingOne": "for 1 paid month",
    "platformOffers.duration.forever": "on every paid invoice",
    "platformOffers.redemption.status.held": "Reserved",
    "platformOffers.redemption.status.scheduled": "Starts when the trial ends",
    "platformOffers.redemption.status.applied": "Applied",
    "platformOffers.redemption.status.released": "Released",
    "platformOffers.redemption.reason.checkout_expired": "checkout not completed",
    "platformOffers.redemption.reason.stripe_failed": "refused by the payment service",
    "platformOffers.redemption.reason.sweep": "reservation expired",
    "platformOffers.redemption.reason.superseded": "replaced by a newer code",
    "platformOffers.redemption.reason.subscription_ended": "subscription ended",
    "platformOffers.redemption.reason.request_failed": "request interrupted",
    "platformOffers.redemption.reason.practice_deleted": "practice deleted",
    "platformOffers.redemption.surface.signup": "Signup",
    "platformOffers.redemption.surface.plan_change": "Plan change",
    "platformOffers.redemption.surface.billing_redeem": "Billing page",
    "platformOffers.redemption.kind.promo": "promo code",
  },
  es: {
    "platformOffers.error.PLATFORM_CODE_INVALID": "Este código no es válido aquí.",
    "platformOffers.error.PLATFORM_CODE_RACE": "Este código se acaba de usar al mismo tiempo en otro lugar. Vuelva a intentarlo.",
    "platformOffers.error.NO_ACTIVE_SUBSCRIPTION": "Un código promocional se aplica a una suscripción activa, y este consultorio no tiene ninguna.",
    "platformOffers.error.NO_STRIPE_CUSTOMER": "Añada primero un medio de pago: el crédito de una tarjeta regalo se registra en su cuenta de facturación.",
    "platformOffers.error.PROMO_CODE_EXISTS": "Este código ya existe.",
    "platformOffers.error.PROMO_TERMS_INVALID": "Las condiciones de esta promoción son incoherentes: revise el tipo, el importe, la duración, los planes y las fechas.",
    "platformOffers.error.GIFT_CARD_NOT_VOIDABLE": "Esta tarjeta ya no se puede anular: está reservada, ya canjeada o ya anulada.",
    "platformOffers.error.GIFT_CARD_VOIDED": "Esta tarjeta está anulada: no hay nada que reenviar.",
    "platformOffers.error.PAYMENT_PROVIDER_UNAVAILABLE": "El servicio de pago rechazó la operación. No se ha modificado nada: vuelva a intentarlo en un momento.",
    "platformOffers.error.REDEMPTION_OUTCOME_UNKNOWN": "El servicio de pago no pudo confirmar la operación. Su código sigue reservado y se aplicará o liberará automáticamente en menos de una hora: no hace falta volver a intentarlo.",
    "platformOffers.error.NOT_FOUND": "Este código ya no existe.",
    "platformOffers.error.RATE_LIMITED": "Demasiados intentos: vuelva a intentarlo en un minuto.",
    "platformOffers.error.generic": "No se pudo comprobar este código. Vuelva a intentarlo.",
    "platformOffers.code.labelAny": "Código promocional o tarjeta regalo",
    "platformOffers.code.labelPromo": "Código promocional",
    "platformOffers.code.labelGiftcard": "Código de tarjeta regalo",
    "platformOffers.code.placeholder": "Introduzca su código",
    "platformOffers.code.apply": "Aplicar",
    "platformOffers.code.remove": "Quitar",
    "platformOffers.code.kindRefused": "Este código no se puede usar aquí.",
    "platformOffers.offer.percentOff": "{percent} de descuento",
    "platformOffers.offer.amountOff": "{amount} de descuento",
    "platformOffers.offer.credit": "{amount} de crédito",
    "platformOffers.offer.perInvoice": "{from} → {to} por factura",
    "platformOffers.duration.once": "en la primera factura pagada",
    "platformOffers.duration.repeating": "durante {months} meses pagados",
    "platformOffers.duration.repeatingOne": "durante 1 mes pagado",
    "platformOffers.duration.forever": "en cada factura pagada",
    "platformOffers.redemption.status.held": "Reservado",
    "platformOffers.redemption.status.scheduled": "Empieza al terminar la prueba",
    "platformOffers.redemption.status.applied": "Aplicado",
    "platformOffers.redemption.status.released": "Liberado",
    "platformOffers.redemption.reason.checkout_expired": "pago no completado",
    "platformOffers.redemption.reason.stripe_failed": "rechazado por el servicio de pago",
    "platformOffers.redemption.reason.sweep": "reserva vencida",
    "platformOffers.redemption.reason.superseded": "sustituido por un código más reciente",
    "platformOffers.redemption.reason.subscription_ended": "suscripción finalizada",
    "platformOffers.redemption.reason.request_failed": "solicitud interrumpida",
    "platformOffers.redemption.reason.practice_deleted": "consulta eliminada",
    "platformOffers.redemption.surface.signup": "Registro",
    "platformOffers.redemption.surface.plan_change": "Cambio de plan",
    "platformOffers.redemption.surface.billing_redeem": "Página de facturación",
    "platformOffers.redemption.kind.promo": "código promocional",
  },
};
